import { useState, Fragment, memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { clsx } from 'clsx';
import { Text } from '@library';
import type { ProductItem } from '../../types';
import VerticalSquaresIcon from '../../../core/components/icons/VerticalSquaresIcon';
import Button from '../../../core/components/Button';
import ClickableArea from '../../../core/components/button/ClickableArea';
import ProductAttributesModal from './ProductAttributesModal';
import styles from './ProductListViewAttributes.module.scss';

const ProductListViewAttributes = ({
    product,
    activeSku,
    shortView,
}: {
    product: ProductItem;
    activeSku?: string;
    shortView?: boolean;
}) => {
    const [showAttributesDialog, setShowAttributesDialog] = useState(false);

    const specificationAttributes =
        (activeSku && activeSku !== product.sku
            ? product.variants.find(({ sku }) => sku === activeSku)
                  ?.specificationAttributes
            : product.specificationAttributes) || [];

    const hasManyAttributes = specificationAttributes.length > 6;

    const attributes = hasManyAttributes
        ? specificationAttributes.slice(0, 5)
        : specificationAttributes;

    return (
        <>
            {shortView ? (
                attributes.length > 0 && (
                    <div className={styles.shortViewButton}>
                        <Button
                            iconButton
                            noPadding
                            onPress={() => {
                                setShowAttributesDialog(true);
                            }}
                        >
                            <VerticalSquaresIcon />
                        </Button>
                    </div>
                )
            ) : (
                <div>
                    <div
                        className={clsx(styles.attributes, {
                            [styles.attributesOverflowingInfoIcon]:
                                attributes.length > 5,
                        })}
                    >
                        {attributes.map(({ name, value }, idx) => (
                            <Fragment key={idx.toString() + name}>
                                <div
                                    className={clsx(
                                        styles.attributeColumn,
                                        styles.attributeNameColumn
                                    )}
                                    title={name}
                                    data-test-class="productAttributeItem"
                                >
                                    <div className={styles.square} />
                                    <div
                                        className={styles.attributeNameWrapper}
                                    >
                                        <Text tertiary medium>
                                            <span
                                                className={styles.attributeName}
                                            >
                                                {name}
                                            </span>
                                        </Text>
                                    </div>
                                </div>
                                <div
                                    className={styles.attributeColumn}
                                    key={idx.toString() + value}
                                    title={value}
                                >
                                    <Text tertiary>
                                        <span className={styles.attributeValue}>
                                            {value}
                                        </span>
                                    </Text>
                                </div>
                            </Fragment>
                        ))}
                    </div>
                    {hasManyAttributes && (
                        <ClickableArea
                            className={styles.showMoreAttributesButton}
                            onClick={() => setShowAttributesDialog(true)}
                        >
                            <div className={styles.square} />
                            <Text tertiary medium>
                                <span className={styles.attributeName}>
                                    <FormattedMessage
                                        id="{count} more features"
                                        values={{
                                            count:
                                                specificationAttributes.length -
                                                5,
                                        }}
                                    />
                                </span>
                            </Text>
                        </ClickableArea>
                    )}
                </div>
            )}

            {showAttributesDialog && (
                <ProductAttributesModal
                    product={product}
                    onClose={() => setShowAttributesDialog(false)}
                />
            )}
        </>
    );
};

export default memo(ProductListViewAttributes);
